import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { db, auth } from '../../firebase';
import { useAuth } from '../../AuthContext';

const STEPS = [
  { id: 'welcome',   label: 'ようこそ' },
  { id: 'nickname',  label: 'ニックネーム' },
  { id: 'guide',     label: '使い方' },
];

const NG_WORDS = ['死ね','殺す','バカ','馬鹿','アホ','クズ','fuck','shit','bitch'];
const containsNg = (t) => NG_WORDS.some(w => t.toLowerCase().includes(w.toLowerCase()));

export default function OnboardingFlow({ onComplete }) {
  const { user } = useAuth();
  const [step, setStep]       = useState(0);
  const [nickname, setNickname] = useState('');
  const [error, setError]     = useState('');
  const [saving, setSaving]   = useState(false);

  const handleNicknameSave = async () => {
    const v = nickname.trim();
    if (!v) { setError('ニックネームを入力してください'); return; }
    if (v.length < 2) { setError('2文字以上で入力してください'); return; }
    if (v.length > 12) { setError('12文字以内で入力してください'); return; }
    if (containsNg(v)) { setError('使用できない言葉が含まれています'); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        nickname: v, username: v, displayName: v,
        nicknameSetAt: serverTimestamp(),
      });
      if (auth.currentUser) await updateProfile(auth.currentUser, { displayName: v });
      setStep(2);
    } catch(e) {
      setError('保存に失敗しました。もう一度お試しください。');
    } finally { setSaving(false); }
  };

  const handleSkipNickname = () => setStep(2);

  const handleComplete = async () => {
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        onboardingCompleted: true,
        onboardingCompletedAt: serverTimestamp(),
      });
    } catch(e) { console.error(e); }
    onComplete();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,12,41,0.92)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 24, width: '100%', maxWidth: 480, overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.35)' }}>

        {/* プログレスバー */}
        <div style={{ height: 4, background: '#f3f4f6' }}>
          <div style={{ height: 4, width: `${((step + 1) / STEPS.length) * 100}%`, background: 'linear-gradient(90deg, #6366f1, #8b5cf6)', transition: 'width 0.4s ease' }} />
        </div>

        {/* ステップ表示 */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: '16px 0 0' }}>
          {STEPS.map((s, i) => (
            <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                background: i <= step ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : '#f3f4f6',
                color: i <= step ? '#fff' : '#9ca3af' }}>
                {i < step ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: 11, color: i === step ? '#6366f1' : '#9ca3af', fontWeight: i === step ? 700 : 400 }}>{s.label}</span>
              {i < STEPS.length - 1 && <div style={{ width: 20, height: 1, background: '#e5e7eb' }} />}
            </div>
          ))}
        </div>

        <div style={{ padding: '24px 32px 32px' }}>

          {/* Step 0: ようこそ */}
          {step === 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
              <h2 style={{ fontSize: 24, fontWeight: 800, color: '#111827', marginBottom: 12 }}>
                NOBISHIROへようこそ！
              </h2>
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.8, marginBottom: 24 }}>
                あなたの<strong style={{ color: '#6366f1' }}>専門家</strong>への挑戦が始まります。<br />
                NOBISHIROは学習を可視化し、<br />
                専門家への道を科学的にサポートします。
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 28 }}>
                {[
                  { emoji: '📊', title: '進捗可視化', desc: '成長目標をグラフで管理' },
                  { emoji: '🧠', title: '診断機能', desc: 'あなたの成長タイプを発見' },
                  { emoji: '🤝', title: 'チーム機能', desc: 'みんなで一緒に成長できる' },
                ].map(f => (
                  <div key={f.title} style={{ background: '#f8fafc', borderRadius: 12, padding: '14px 10px', textAlign: 'center', border: '1px solid #e5e7eb' }}>
                    <div style={{ fontSize: 24, marginBottom: 6 }}>{f.emoji}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 4 }}>{f.title}</div>
                    <div style={{ fontSize: 10, color: '#9ca3af', lineHeight: 1.5, whiteSpace: 'pre-line' }}>{f.desc}</div>
                  </div>
                ))}
              </div>
              <button onClick={() => setStep(1)}
                style={{ width: '100%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 14, padding: '14px', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 16px rgba(99,102,241,0.4)' }}>
                はじめる →
              </button>
            </div>
          )}

          {/* Step 1: ニックネーム */}
          {step === 1 && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>✏️</div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginBottom: 8 }}>
                  ニックネームを設定しよう
                </h2>
                <p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.7 }}>
                  達成フィードやチームで表示されます。<br />
                  後から変更できます。
                </p>
              </div>

              {/* プレビュー */}
              <div style={{ background: '#f0f9ff', borderRadius: 12, padding: '12px 16px', marginBottom: 20, border: '1px solid #bae6fd' }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#0369a1', marginBottom: 6 }}>表示イメージ</div>
                <div style={{ fontSize: 13, color: '#374151' }}>
                  <strong style={{ color: '#6366f1' }}>{nickname.trim() || 'あなたの名前'}</strong>
                  さんが100hを達成！🎉
                </div>
              </div>

              <div style={{ marginBottom: 8 }}>
                <input
                  type="text"
                  value={nickname}
                  onChange={e => { setNickname(e.target.value); setError(''); }}
                  placeholder="例：とみー、田中太郎、匿名希望"
                  maxLength={12}
                  autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleNicknameSave()}
                  style={{ width: '100%', border: `2px solid ${error ? '#ef4444' : '#6366f1'}`, borderRadius: 12, padding: '12px 16px', fontSize: 15, outline: 'none', color: '#111827', boxSizing: 'border-box' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
                  {error
                    ? <span style={{ fontSize: 12, color: '#ef4444' }}>⚠️ {error}</span>
                    : <span style={{ fontSize: 12, color: '#9ca3af' }}>2〜12文字・後から変更可能</span>
                  }
                  <span style={{ fontSize: 12, color: nickname.length > 10 ? '#f59e0b' : '#9ca3af' }}>{nickname.length}/12</span>
                </div>
              </div>

              <button onClick={handleNicknameSave} disabled={saving || !nickname.trim()}
                style={{ width: '100%', background: nickname.trim() ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' : '#e5e7eb', color: nickname.trim() ? '#fff' : '#9ca3af', border: 'none', borderRadius: 14, padding: '13px', fontSize: 15, fontWeight: 800, cursor: nickname.trim() ? 'pointer' : 'not-allowed', marginBottom: 10, boxShadow: nickname.trim() ? '0 4px 16px rgba(99,102,241,0.3)' : 'none' }}>
                {saving ? '保存中...' : 'このニックネームで続ける'}
              </button>
              <button onClick={handleSkipNickname}
                style={{ width: '100%', background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, cursor: 'pointer', padding: 6 }}>
                今はスキップ（後で設定できます）
              </button>
            </div>
          )}

          {/* Step 2: 使い方ガイド */}
          {step === 2 && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🚀</div>
                <h2 style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginBottom: 8 }}>
                  まず何から始める？
                </h2>
                <p style={{ fontSize: 13, color: '#6b7280' }}>最初のステップをご案内します</p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                {[
                  { step: '① おすすめ', emoji: '🧠', title: 'NOBISHIRO診断を受ける', desc: '約5分・あなたの成長タイプと強みがわかります', color: '#6366f1', href: '/diagnosis' },
                  { step: '② 学習記録', emoji: '📝', title: '学習ログを記録する', desc: 'サマリータブから毎日の学習を記録しましょう', color: '#10b981', href: null },
                  { step: '③ まず20h', emoji: '🎯', title: '最初の20hを目指そう', desc: 'まずは20h達成！小さな成功体験が継続の鍵です', color: '#f59e0b', href: null },
                ].map(g => (
                  <div key={g.title} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', background: '#f8fafc', borderRadius: 12, border: `1px solid ${g.color}20` }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: g.color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, flexShrink: 0 }}>{g.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: g.color, fontWeight: 700, marginBottom: 2 }}>{g.step}</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>{g.title}</div>
                      <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{g.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button onClick={handleComplete}
                style={{ width: '100%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', border: 'none', borderRadius: 14, padding: '14px', fontSize: 16, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 16px rgba(99,102,241,0.4)' }}>
                ダッシュボードへ進む 🎉
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
